/**
 * Auth slice — manages the currently authenticated user and auth lifecycle.
 *
 * The `User` type is imported from `shared/api/types/user` (the canonical
 * Django-aligned shape that matches `UserMeSerializer`). It is re-exported
 * from the `shared` barrel as `ApiUser` to avoid colliding with the older
 * mobile-only `User` shape; here we import it directly from the sub-path
 * so we can name it `User` locally without the alias noise.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { User } from 'shared/api/types/user';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'error';

export interface AuthState {
  /** Authenticated user, or `null` when not logged in / loading. */
  user: User | null;
  /**
   * Auth lifecycle status:
   *   idle          — initial state, no auth attempt yet
   *   loading       — login / refresh in progress
   *   authenticated — user is logged in and `user` is populated
   *   error         — last auth attempt failed
   */
  status: AuthStatus;
  /** Error message from the last failed auth operation. */
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  status: 'idle',
  error: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /** Called when a login / token-refresh request begins. */
    authLoading(state) {
      state.status = 'loading';
      state.error = null;
    },
    /** Called when a user is successfully authenticated (login, signup, /me refresh). */
    setUser(state, action: PayloadAction<User>) {
      state.user = action.payload;
      state.status = 'authenticated';
      state.error = null;
    },
    /** Called on logout or token expiry with no silent refresh possible. */
    clearUser(state) {
      state.user = null;
      state.status = 'idle';
      state.error = null;
    },
    /** Called when an auth operation fails (wrong password, network error, etc.). */
    authError(state, action: PayloadAction<string>) {
      state.status = 'error';
      state.error = action.payload;
    },
  },
});

export const { authLoading, setUser, clearUser, authError } = authSlice.actions;

export default authSlice.reducer;
